import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  pending:  'bg-yellow-50 text-yellow-700 border-yellow-200',
  reviewed: 'bg-blue-50 text-blue-700 border-blue-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
  closed:   'bg-gray-50 text-gray-500 border-gray-200',
};

export default function AdminFeedbacks() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats]         = useState({});
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState({ status: '', category: '' });
  const [replyModal, setReplyModal] = useState(null);
  const [reply, setReply]         = useState('');
  const [replyStatus, setReplyStatus] = useState('resolved');
  const [saving, setSaving]       = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filter).filter(([,v]) => v)));
      const [fbRes, statsRes] = await Promise.all([
        api.get(`/feedback/all?${params}`),
        api.get('/feedback/stats'),
      ]);
      setFeedbacks(fbRes.feedbacks || []);
      setStats(statsRes.stats || {});
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const handleReply = async () => {
    if (!reply.trim()) return toast.error('Please write a reply');
    setSaving(true);
    try {
      await api.put(`/feedback/${replyModal.id}/reply`, { admin_reply: reply, status: replyStatus });
      toast.success('Reply sent!');
      setReplyModal(null);
      setReply('');
      load();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const stars = (n) => [1,2,3,4,5].map(s =>
    <span key={s} className={`text-base ${n >= s ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">📝 Citizen Feedbacks</h1>
        <p className="text-sm text-gray-500 mt-1">Review and respond to citizen feedback</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total', value: stats.total || 0, color: 'blue', icon: '📊' },
          { label: 'Pending', value: stats.pending || 0, color: 'yellow', icon: '⏳' },
          { label: 'Resolved', value: stats.resolved || 0, color: 'green', icon: '✅' },
          { label: 'Avg Rating', value: stats.avg_rating ? `${stats.avg_rating}★` : '—', color: 'purple', icon: '⭐' },
        ].map(s => (
          <div key={s.label} className="card py-4 text-center">
            <p className="text-2xl mb-1">{s.icon}</p>
            <p className="text-2xl font-bold text-gray-800">{s.value}</p>
            <p className="text-xs text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card mb-4 py-3">
        <div className="flex gap-3 flex-wrap">
          <select className="form-select text-sm" value={filter.status}
            onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select className="form-select text-sm" value={filter.category}
            onChange={e => setFilter(f => ({ ...f, category: e.target.value }))}>
            <option value="">All Categories</option>
            <option value="general">General</option>
            <option value="application">Application</option>
            <option value="service">Service</option>
            <option value="technical">Technical</option>
            <option value="suggestion">Suggestion</option>
            <option value="complaint">Complaint</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-5xl mb-3">📭</p>
          <p className="text-lg font-semibold text-gray-700">No feedbacks found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {feedbacks.map(fb => (
            <div key={fb.id} className="card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-gray-800">{fb.subject}</span>
                    <span className={`text-xs border px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[fb.status] || STATUS_COLORS.pending}`}>
                      {fb.status}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full capitalize">
                      {fb.category}
                    </span>
                  </div>
                  <div className="flex gap-0.5 mb-2">{stars(fb.rating)}</div>
                  <div className="flex gap-3 text-xs text-gray-400 mb-2">
                    <span>👤 {fb.user_name}</span>
                    <span>📱 {fb.mobile}</span>
                    <span>📅 {new Date(fb.created_at).toLocaleDateString('en-IN')}</span>
                  </div>
                  <p className="text-sm text-gray-600">{fb.message}</p>
                  {fb.admin_reply && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">
                      <p className="font-semibold text-xs mb-1">✅ Your Reply:</p>
                      {fb.admin_reply}
                    </div>
                  )}
                </div>
                <button onClick={() => { setReplyModal(fb); setReply(fb.admin_reply || ''); setReplyStatus(fb.status); }}
                  className="btn-primary text-xs flex-shrink-0">
                  {fb.admin_reply ? '✏️ Edit Reply' : '💬 Reply'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply Modal */}
      {replyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex justify-between mb-4">
                <h3 className="font-bold text-lg text-gray-800">💬 Reply to Feedback</h3>
                <button onClick={() => setReplyModal(null)} className="text-gray-400 text-2xl leading-none">✕</button>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl mb-4 text-sm text-gray-700">
                <p className="font-medium">{replyModal.subject}</p>
                <p className="text-gray-500 mt-1">{replyModal.message}</p>
              </div>
              <div className="mb-4">
                <label className="form-label">Your Reply</label>
                <textarea className="form-input" rows={4} value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder="Write your response to the citizen..." />
              </div>
              <div className="mb-4">
                <label className="form-label">Update Status</label>
                <select className="form-select" value={replyStatus} onChange={e => setReplyStatus(e.target.value)}>
                  <option value="reviewed">Reviewed</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button onClick={handleReply} disabled={saving} className="btn-primary flex-1">
                  {saving ? '⏳ Saving...' : '📤 Send Reply'}
                </button>
                <button onClick={() => setReplyModal(null)} className="btn-secondary px-6">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}